/**
 * Date formatting for Myshield — always Europe/London (UK GMT/BST).
 * API timestamps without a timezone are treated as UTC.
 */

const LONDON = 'Europe/London';

/** Parse API timestamps. Bare datetimes (no Z/offset) are treated as UTC. */
export function parseApiTimestamp(value: string | Date): Date {
  if (value instanceof Date) return value;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  // Date-only calendar strings stay as local calendar days (no shift).
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0); // noon local avoids DST edge flips
  }
  return new Date(hasTimezone ? value : `${value}Z`);
}

function londonParts(date: Date): Record<string, string> | null {
  if (Number.isNaN(date.getTime())) return null;
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: LONDON,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  ) as Record<string, string>;
}

/**
 * DD/MM/YYYY in Europe/London.
 * Pure YYYY-MM-DD strings keep their calendar day (no timezone shift).
 */
export const formatDateBritish = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [y, m, d] = date.split('-');
      return `${d}/${m}/${y}`;
    }
    const parts = londonParts(parseApiTimestamp(date));
    if (!parts) return '';
    return `${parts.day}/${parts.month}/${parts.year}`;
  } catch {
    return '';
  }
};

/**
 * DD/MM/YYYY HH:mm in Europe/London (UK time).
 */
export const formatDateTimeBritish = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    const parts = londonParts(parseApiTimestamp(date));
    if (!parts) return '';
    return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
  } catch {
    return '';
  }
};

/** DD/MM/YYYY HH:mm UK — explicit label for policy/schedule surfaces */
export const formatLondonDateTime = (date: string | Date | null | undefined): string => {
  const formatted = formatDateTimeBritish(date);
  return formatted ? `${formatted} UK` : '';
};

export const formatLondonDate = (date: string | Date | null | undefined): string =>
  formatDateBritish(date);

export const formatLondonTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return '00:00 UK';
    const parts = londonParts(parseApiTimestamp(date));
    if (!parts) return '';
    return `${parts.hour}:${parts.minute} UK`;
  } catch {
    return '';
  }
};

/**
 * Convert a date input value (YYYY-MM-DD) to British display format (DD/MM/YYYY)
 */
export const inputToDisplayDate = (dateInputValue: string): string => {
  if (!dateInputValue) return '';
  return formatDateBritish(dateInputValue);
};

/**
 * Convert British format date (DD/MM/YYYY) to input value format (YYYY-MM-DD)
 */
export const displayToInputDate = (britishDate: string): string => {
  if (!britishDate) return '';
  const parts = britishDate.split('/');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};
